import { GameIcon } from "../assets/gameIcons.js";
import { Icon as FunctionalIcon } from "../ui/Icon.js";
import { useEffect, useState } from "react";
import { pick, useT } from "../i18n/index.js";
import { Panel } from "../ui/Panel.js";
import { Button } from "../ui/Button.js";
import { Timer, useServerTick } from "../ui/Timer.js";
import { navigate } from "../router.js";
import { useStore } from "../store/state.js";
import { BundleList } from "../store/cards.js";
import { useEvents } from "./state.js";
import { eventsApi } from "./api.js";
import type { EventRunView, MilestoneView } from "./types.js";
import { SiegePanel } from "./SiegePanel.js";
import { PromoArt } from "../store/PromoArt.js";
import { eventDisplayState, orderedEventRuns } from "./presentation.js";

export function sourceText(source: string, t: ReturnType<typeof useT>): string {
  const key = `events.source.${source.replace(".", "_")}`;
  const text = t(key);
  return text === key ? source : text;
}

/** Milestone chests with claim buttons; shared by events, the daily loop and Dawn Protocol. */
export function Milestones({ runId, milestones, points, onChange, testPrefix, compact = false }: { runId: string; milestones: MilestoneView[]; points: number; onChange: () => void; testPrefix: string; compact?: boolean }) {
  const t = useT();
  const run = useEvents((s) => s.run);
  const celebrate = useStore((s) => s.celebrate);
  const claim = async (index: number) => {
    const res = await run(() => eventsApi.claimMilestone(runId, index), t("events.toast.milestone"));
    if (res) {
      celebrate({ key: runId, granted: res.granted, doubled: false, vipPoints: 0, nextStep: null });
      onChange();
    }
  };
  const next = milestones.find(m => !m.claimed);
  const lastClaimed = [...milestones].reverse().find(m => m.claimed);
  const visible = compact ? milestones.filter(m => m.claimable || m.index === next?.index || m.index === lastClaimed?.index) : milestones;
  const remaining = milestones.filter(m => !visible.includes(m));
  const last = (compact ? next?.points : null) ?? milestones[milestones.length - 1]?.points ?? 1;
  const renderMilestone = (m: MilestoneView) => (
          <div key={m.index} className={`milestone ${points >= m.points ? "reached" : ""} ${m.claimed ? "claimed" : ""}`} data-testid={`${testPrefix}-milestone-${m.index}`}>
            <div className="row">
              <span className="mono">{m.points} P</span>
              <span className="grow" />
              {m.claimable ? (
                <Button variant="gold" onClick={() => void claim(m.index)} data-testid={`${testPrefix}-claim-${m.index}`}>
                  {t("common.claim")}
                </Button>
              ) : m.claimed ? (
                <span className="dim"><FunctionalIcon name="check" /></span>
              ) : null}
            </div>
            <BundleList bundle={m.reward} compact />
          </div>
  );
  return (
    <div className={compact ? "event-milestones-compact" : ""}>
      <div className="progress" style={{ margin: "6px 0" }}><div className="progress-fill gold" style={{ width: `${Math.min(100, (points / last) * 100)}%` }} /></div>
      <div className="milestones">{visible.map(renderMilestone)}</div>
      {remaining.length ? <details className="event-more-rewards"><summary>{t("events.redesign.allRewards")} <span>{milestones.length}</span><FunctionalIcon name="chevronDown" size={16} /></summary><div className="milestones">{remaining.map(renderMilestone)}</div></details> : null}
    </div>
  );
}

function Challenge({ run, onChange }: { run: EventRunView; onChange: () => void }) {
  const t = useT();
  const go = useEvents((s) => s.run);
  const celebrate = useStore((s) => s.celebrate);
  const c = run.challenge;
  if (!c) return null;
  if (c.kind === "siege") {
    return <SiegePanel key={run.runId} run={run} onChange={onChange} />;
  }
  if (c.kind === "trial") {
    const climb = async () => {
      const res = await go(() => eventsApi.trial(run.runId));
      if (res) {
        if (res.chest) celebrate({ key: run.runId, granted: res.chest, doubled: false, vipPoints: 0, nextStep: null });
        navigate(`/app/battle/${res.battleId}`);
        onChange();
      }
    };
    return (
      <div className="section" data-testid="challenge-trial">
        <h3>{t("events.trial.title", { floor: c.floor ?? 1, floors: c.floors ?? 100 })}</h3>
        <div className="row" style={{ gap: 8 }}>
          <span className="dim" style={{ fontSize: 12 }}>{t("events.trial.hint", { n: c.staminaPerFloor ?? 5 })}</span>
          <span className="grow" />
          {c.done ? <span className="badge gold">{t("events.trial.cleared")}</span> : <Button variant="gold" onClick={() => void climb()} data-testid="trial-climb">{t("events.trial.climb")}</Button>}
        </div>
      </div>
    );
  }
  const claim = async (index: number) => {
    const res = await go(() => eventsApi.relic(run.runId, index));
    if (res) {
      navigate(`/app/battle/${res.battleId}`);
      onChange();
    }
  };
  return (
    <div className="section" data-testid="challenge-relics">
      <h3>{t("events.relics.title")}</h3>
      <div className="relics">
        {(c.relics ?? []).map((r) => (
          <div key={r.index} className={`relic ${r.mine ? "mine" : ""}`} data-testid={`relic-${r.index}`}>
            <div className="mono">#{r.index + 1}</div>
            <div className="dim" style={{ fontSize: 11 }}>{r.holderName ? t("events.relics.held", { name: r.holderName }) : t("events.relics.free")}</div>
            <div className="dim" style={{ fontSize: 11 }}>{t("events.relics.guard", { level: r.guardLevel })}</div>
            {!r.mine ? <Button onClick={() => void claim(r.index)} data-testid={`relic-claim-${r.index}`}>{t("events.relics.take")}</Button> : <span className="badge gold">{t("events.relics.mine")}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

/** One event: header with countdown and phases, progress and milestones, ranking, how-to and challenge. */
export function EventCard({ run, onChange }: { run: EventRunView; onChange: () => void }) {
  const t = useT();
  const [howTo, setHowTo] = useState(false);
  const now = useServerTick(1);
  const state = eventDisplayState(run, now, run.milestones.some(m => m.claimable));
  const effectiveManufacturer = useEvents((s) => s.modifiers.find((modifier) => modifier.kind === "manufacturer.bonus")?.runId);
  const manufacturerBonus = run.modifier?.kind === "manufacturer.bonus";
  const modifierEffective = !manufacturerBonus || effectiveManufacturer === run.runId;
  return (
    <Panel className="event-detail">
      <div className="event-hero"><PromoArt id="event-frontier" /><div className="event-hero-copy"><span className={`event-state ${state}`}>{t(`events.redesign.${state}`)}</span><h2><GameIcon id={run.icon} size={44} /> {pick(run.name)}</h2>{run.endsAt > now ? <span className="event-time" data-testid="event-countdown" data-ends-at={run.endsAt}><FunctionalIcon name="clock" /> {t(state === "upcoming" ? "events.redesign.starts" : "events.redesign.ends")} <Timer finishesAt={state === "upcoming" ? run.startsAt : run.endsAt} /></span> : null}</div></div>
      <p className="hint">{pick(run.description)}</p>
      {run.phases.length > 1 ? (
        <div className="row phases">
          {run.phases.map((p) => (
            <span key={`${p.id}-${p.startsAt}`} className={`badge ${p.active ? "gold" : ""}`}>
              {pick(p.name)}
            </span>
          ))}
        </div>
      ) : null}
      {run.modifier ? <span className={modifierEffective ? "badge" : "hint"} data-testid={manufacturerBonus ? "manufacturer-bonus-status" : undefined} data-effective={modifierEffective}>
        {modifierEffective ? t(`events.modifier.${run.modifier.kind.replace(".", "_")}`, { v: String(run.modifier.value), d: run.modifier.detail ?? "" }) : t("events.manufacturerInactive")}
      </span> : null}
      <div className="row" style={{ gap: 10, marginTop: 8 }}>
        <span className="mono" data-testid={`event-points-${run.defId}`}>{t("events.points", { n: run.points })}</span>
        {run.rank ? <span className="badge gold">#{run.rank}</span> : null}
        <span className="grow" />
        <button type="button" className="plain dim" onClick={() => setHowTo((v) => !v)} data-testid={`event-howto-${run.defId}`}>
          {t("events.howto")}
        </button>
      </div>
      {howTo ? (
        <ul className="perks" data-testid="event-howto-list">
          {run.scoring.map((s, i) => (
            <li key={i}>
              {sourceText(s.source, t)}: +{s.points} P{s.per ? ` / ${s.per}` : ""}
              {s.theme ? ` (${s.theme})` : ""}
              {s.cap ? ` · max ${s.cap}/d` : ""}
            </li>
          ))}
          {run.tokensPer100 ? <li>{t("events.tokens", { n: run.tokensPer100 })}</li> : null}
        </ul>
      ) : null}
      {run.milestones.length ? <section className="event-next-reward"><h3>{t("events.redesign.nextReward")}</h3><Milestones compact runId={run.runId} milestones={run.milestones} points={run.points} onChange={onChange} testPrefix={`event-${run.defId}`} /></section> : null}
      {run.ranking.rewards.length ? (
        <details className="event-ranking"><summary>{t("events.redesign.ranking")}<FunctionalIcon name="chevronDown" size={16} /></summary>
          {run.ranking.top.length ? (
            <ol className="ranking">
              {run.ranking.top.slice(0, 10).map((r) => (
                <li key={r.accountId} className="row">
                  <span className="mono" style={{ width: 28 }}>{r.rank}</span>
                  <span className="grow ellipsis">{r.name}</span>
                  <span className="mono dim">{r.points}</span>
                </li>
              ))}
            </ol>
          ) : (
            <p className="hint">{t("events.ranking.empty")}</p>
          )}
          <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
            {run.ranking.rewards.slice(0, 4).map((b) => (
              <span key={b.fromRank} className="badge">
                #{b.fromRank}{b.toRank !== b.fromRank ? `–${b.toRank >= 1_000_000 ? "∞" : b.toRank}` : ""}: {b.reward.credits ? `₵${b.reward.credits} ` : ""}{b.reward.items?.length ?? 0} {t("events.items")}
              </span>
            ))}
          </div>
        </details>
      ) : null}
      {run.shop ? (
        <Button variant="ghost" onClick={() => navigate("/app/store/shops")} data-testid={`event-shop-${run.defId}`}>
          {t("events.shop")}
        </Button>
      ) : null}
      <Challenge run={run} onChange={onChange} />
    </Panel>
  );
}

/** The Event Plaza: one tab per active event (R63). */
export function PlazaView({ requestedRunId }: { requestedRunId?: string | undefined }) {
  const t = useT();
  const runs = useEvents((s) => s.runs);
  const refreshRuns = useEvents((s) => s.refreshRuns);
  const [active, setActive] = useState<string | null>(requestedRunId ?? null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const now = useServerTick(1);
  useEffect(() => { setActive(requestedRunId ?? null); setPickerOpen(false); }, [requestedRunId]);
  if (!runs) return <p className="hint">{t("common.loading")}</p>;
  const visible = orderedEventRuns(runs.filter((r) => r.category !== "daily"), now);
  const current = visible.find((r) => r.runId === active) ?? visible.find(r => r.runId === requestedRunId) ?? visible[0] ?? null;
  return (
    <div data-testid="plaza" className="plaza-view">
      <div className="event-overview-heading"><h1>{t("events.redesign.title")}</h1><p>{t("events.redesign.subtitle")}</p></div>
      <details className="plaza-picker" open={pickerOpen} onToggle={event => setPickerOpen(event.currentTarget.open)}>
        <summary data-testid="plaza-picker"><FunctionalIcon name="calendar" size={20} /><span>{t("events.redesign.overview")} <b>{visible.length}</b></span><FunctionalIcon name="chevronDown" size={18} /></summary>
        <div className="plaza-options">
        {visible.map((r) => (
          <button key={r.runId} type="button" className={`plaza-option ${r.runId === current?.runId ? "selected" : ""}`} aria-pressed={r.runId === current?.runId} onClick={() => { setActive(r.runId); setPickerOpen(false); }} data-testid={`plaza-tab-${r.defId}`} data-run-id={r.runId}>
            <GameIcon id={r.icon} size={38} /><span><strong>{pick(r.name)}</strong><small className={`event-state ${eventDisplayState(r, now, r.milestones.some(m => m.claimable))}`}>{t(`events.redesign.${eventDisplayState(r, now, r.milestones.some(m => m.claimable))}`)}</small></span><FunctionalIcon name="chevronRight" size={16} />
          </button>
        ))}
        </div>
      </details>
      {current ? <EventCard key={current.runId} run={current} onChange={() => void refreshRuns()} /> : <p className="hint">{t("events.none")}</p>}
    </div>
  );
}
